import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {


  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  Store as ServiceIcon,
  PhoneAndroid as MobileIcon,
  Settings as SettingsIcon,
  Smartphone as SmartphoneIcon,
  Speed as SpeedIcon,
  Bolt as BoltIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Wifi as WifiIcon,
  WaterDrop as WaterDropIcon,
  LocalGasStation as LocalGasStationIcon,
  ConfirmationNumber as ConfirmationNumIcon,
  VolunteerActivism as VolunteerActivismIcon,
  Shield as ShieldIcon,
  AccountBalance as AccountBalanceIcon,
  ShoppingBag as ShoppingBagIcon,
  Restaurant as RestaurantIcon,
  Store as StoreIcon,
  ReceiptLong as ReceiptLongIcon,
  CreditCard as CreditCardIcon,
  Payments as PaymentsIcon,
  School as SchoolIcon,
  LocalHospital as LocalHospitalIcon,
  DirectionsBus as DirectionsBusIcon,
  Flight as FlightIcon,
  SportsSoccer as SportsSoccerIcon,
  MusicNote as MusicNoteIcon,
  Groups as GroupsIcon,
  Home as HomeIcon,
  Pets as PetsIcon,
  Park as EcoIcon,
  Church as ChurchIcon,
  Apps as AppsIcon,
  MiscellaneousServices as MiscellaneousServicesIcon,
} from '@mui/icons-material';
import { auth } from '../../../config/firebase';

/**
 * Cabeçalhos com o token do utilizador.
 *
 * As rotas `/v1/admin/services` deixaram de ser públicas — tinham 0 de 17 rotas
 * protegidas, e um POST sem token criava registos. Fechado o servidor, todos os
 * ecrãs que falavam por `fetch` cru têm de passar a enviar o token.
 */
async function cabecalhos(): Promise<Record<string, string>> {
  const base: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    await auth.authStateReady();
    const utilizador = auth.currentUser;
    if (utilizador) base.Authorization = `Bearer ${await utilizador.getIdToken()}`;
  } catch (erro) {
    console.error('[admin] não foi possível obter o token:', erro);
  }
  return base;
}

interface ServiceCategory {
  id: number;
  nome: string;
  estado: string;
  servicosCount: number;
  // Mobile fields
  publicadoMobile?: boolean;
  iconeUrl?: string;
  cor?: string;
  ordem?: number;
  codigo?: string;
  rotaMobile?: string;
  referenceLabel?: string;
  referenceHint?: string;
}

interface Stats {
  total: number;
  ativas: number;
  inativas: number;
  totalServicos: number;
}

// Cores predefinidas para categorias mobile
const CATEGORY_COLORS = [
  { label: 'Verde Teal', value: '#009688' },
  { label: 'Verde', value: '#4CAF50' },
  { label: 'Azul', value: '#2196F3' },
  { label: 'Roxo', value: '#9C27B0' },
  { label: 'Rosa', value: '#E91E63' },
  { label: 'Laranja', value: '#FF9800' },
  { label: 'Vermelho', value: '#F44336' },
  { label: 'Indigo', value: '#3F51B5' },
  { label: 'Cyan', value: '#00BCD4' },
  { label: 'Amarelo', value: '#FFC107' },
  { label: 'Castanho', value: '#795548' },
  { label: 'Cinza', value: '#607D8B' },
];

const API_URL = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';

const ServiceCategories: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    ativas: 0,
    inativas: 0,
    totalServicos: 0,
  });
  const [hasMobileSupport, setHasMobileSupport] = useState(false);
  const [activatingMobile, setActivatingMobile] = useState(false);

  // Edit dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    estado: 'A',
    codigo: '',
    cor: '#009688',
    ordem: 1,
    iconeUrl: '',
    rotaMobile: '',
    publicadoMobile: true,
    referenceLabel: '',
    referenceHint: '',
  });
  const [saving, setSaving] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message: string, severity: 'success' | 'error') => {
    setToast({ open: true, message, severity });
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/v1/admin/services/categories?all=true`, { headers: await cabecalhos() });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCategories(data.data || []);
          if (data.stats) {
            setStats(data.stats);
          }
          // Verifica se o backend suporta campos mobile
          setHasMobileSupport(data.hasMobileSupport === true);
        }
      } else {
        throw new Error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showToast(t('settings.serviceCategories.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  const activateMobileSupport = async () => {
    if (!window.confirm(t('settings.serviceCategories.activateMobileConfirm'))) {
      return;
    }

    setActivatingMobile(true);
    try {
      const response = await fetch(`${API_URL}/v1/admin/migrate/mobile-categories`, { headers: await cabecalhos() });
      const data = await response.json();

      if (data.success) {
        showToast(t('settings.serviceCategories.mobileActivated'), 'success');
        fetchCategories();
      } else {
        showToast(data.error || t('settings.serviceCategories.errorActivatingMobile'), 'error');
      }
    } catch (error) {
      console.error('Error activating mobile support:', error);
      showToast(t('settings.serviceCategories.errorActivatingMobile'), 'error');
    } finally {
      setActivatingMobile(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const getStatusChip = (estado: string) => {
    return estado === 'A' ? (
      <Chip size="small" color="success" label={t('settings.serviceCategories.activeFemaleStatus')} icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label={t('settings.serviceCategories.inactiveFemaleStatus')} icon={<InactiveIcon />} />
    );
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (category?: ServiceCategory) => {
    if (category) {
      setSelectedCategory(category);
      setFormData({
        nome: category.nome,
        estado: category.estado,
        codigo: category.codigo || '',
        cor: category.cor || '#009688',
        ordem: category.ordem || 1,
        iconeUrl: category.iconeUrl || '',
        rotaMobile: category.rotaMobile || '',
        publicadoMobile: category.publicadoMobile ?? true,
        referenceLabel: category.referenceLabel || '',
        referenceHint: category.referenceHint || '',
      });
    } else {
      setSelectedCategory(null);
      setFormData({
        nome: '',
        estado: 'A',
        codigo: '',
        cor: '#009688',
        ordem: categories.length + 1,
        iconeUrl: '',
        rotaMobile: '',
        publicadoMobile: true,
        referenceLabel: '',
        referenceHint: '',
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedCategory(null);
  };

  const handleSave = async () => {
    if (!formData.nome) {
      showToast(t('settings.serviceCategories.nameRequired'), 'error');
      return;
    }

    setSaving(true);
    try {
      const url = selectedCategory
        ? `${API_URL}/v1/admin/services/categories/${selectedCategory.id}`
        : `${API_URL}/v1/admin/services/categories`;

      const method = selectedCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: await cabecalhos(),
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        showToast(
          selectedCategory ? t('settings.serviceCategories.categoryUpdated') : t('settings.serviceCategories.categoryCreated'),
          'success'
        );
        handleCloseEdit();
        fetchCategories();
      } else {
        showToast(data.error || t('settings.serviceCategories.errorSaving'), 'error');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      showToast(t('settings.serviceCategories.errorSaving'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category: ServiceCategory) => {
    if (category.servicosCount > 0) {
      showToast(t('settings.serviceCategories.cannotDelete', { count: category.servicosCount }), 'error');
      return;
    }

    if (!window.confirm(t('settings.serviceCategories.confirmDelete', { name: category.nome }))) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/v1/admin/services/categories/${category.id}`, {
        method: 'DELETE',
        headers: await cabecalhos(),
      });

      const data = await response.json();

      if (data.success) {
        showToast(t('settings.serviceCategories.categoryDeleted'), 'success');
        fetchCategories();
      } else {
        showToast(data.error || t('settings.serviceCategories.errorDeleting'), 'error');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showToast(t('settings.serviceCategories.errorDeleting'), 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t('settings.serviceCategories.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('settings.serviceCategories.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!hasMobileSupport && (
            <Tooltip title={t('settings.serviceCategories.activateMobileTooltip')}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={activatingMobile ? <CircularProgress size={16} /> : <MobileIcon />}
                onClick={activateMobileSupport}
                disabled={activatingMobile}
              >
                {activatingMobile ? t('settings.serviceCategories.activating') : t('settings.serviceCategories.activateMobile')}
              </Button>
            </Tooltip>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchCategories}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            {t('settings.serviceCategories.newCategory')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.serviceCategories.totalCategories')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.ativas}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.serviceCategories.activeFemale')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="disabled" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inativas}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.serviceCategories.inactiveFemale')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ServiceIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.totalServicos}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.serviceCategories.totalServices')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Categories Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={60} align="center">ID</TableCell>
                    <TableCell>{t('common.name')}</TableCell>
                    {hasMobileSupport && (
                      <>
                        <TableCell width={80} align="center">{t('settings.serviceCategories.order')}</TableCell>
                        <TableCell width={80} align="center">{t('common.code')}</TableCell>
                        <TableCell width={60} align="center">{t('settings.serviceCategories.color')}</TableCell>
                        <TableCell width={80} align="center">Mobile</TableCell>
                      </>
                    )}
                    <TableCell width={100} align="center">{t('settings.serviceCategories.services')}</TableCell>
                    <TableCell width={100}>{t('common.status')}</TableCell>
                    <TableCell width={100} align="center">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={hasMobileSupport ? 9 : 5} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          {t('settings.serviceCategories.noCategories')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    categories
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((category) => (
                        <TableRow key={category.id} hover>
                          <TableCell align="center">
                            <Chip size="small" label={category.id} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              {category.iconeUrl ? (
                                <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: category.cor ? `${category.cor}20` : 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <img src={category.iconeUrl} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                                </Box>
                              ) : category.cor ? (
                                <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: `${category.cor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: category.cor }} />
                                </Box>
                              ) : null}
                              <Typography variant="body2" fontWeight={600}>
                                {category.nome}
                              </Typography>
                            </Box>
                          </TableCell>
                          {hasMobileSupport && (
                            <>
                              <TableCell align="center">
                                <Chip size="small" label={category.ordem || '-'} variant="outlined" />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {category.codigo || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {category.cor ? (
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      backgroundColor: category.cor,
                                      border: '2px solid #fff',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                      mx: 'auto',
                                    }}
                                  />
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {category.publicadoMobile ? (
                                  <Chip size="small" color="success" label={t('common.yes')} icon={<MobileIcon />} />
                                ) : (
                                  <Chip size="small" color="default" label={t('common.no')} />
                                )}
                              </TableCell>
                            </>
                          )}
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={category.servicosCount}
                              color={category.servicosCount > 0 ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{getStatusChip(category.estado)}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => handleOpenEdit(category)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(category)}
                              disabled={category.servicosCount > 0}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={categories.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={t('common.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
            />
          </>
        )}
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon />
          {selectedCategory ? t('settings.serviceCategories.editCategory') : t('settings.serviceCategories.newCategory')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.serviceCategories.categoryName')}
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              >
                <MenuItem value="A">{t('settings.serviceCategories.activeFemaleStatus')}</MenuItem>
                <MenuItem value="I">{t('settings.serviceCategories.inactiveFemaleStatus')}</MenuItem>
              </TextField>
            </Grid>

            <>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MobileIcon fontSize="small" />
                    {t('settings.serviceCategories.mobileConfig')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label={t('common.code')}
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                    placeholder="Ex: RCG"
                    helperText={t('settings.serviceCategories.codeHelper')}
                    inputProps={{ maxLength: 5, style: { textTransform: 'uppercase' } }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('settings.serviceCategories.order')}
                    value={formData.ordem}
                    onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 1 })}
                    inputProps={{ min: 1 }}
                    helperText={t('settings.serviceCategories.orderHelper')}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    select
                    label={t('settings.serviceCategories.color')}
                    value={formData.cor}
                    onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                    helperText={t('settings.serviceCategories.colorHelper')}
                  >
                    {CATEGORY_COLORS.map((color) => (
                      <MenuItem key={color.value} value={color.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: color.value,
                              border: '1px solid #ddd',
                            }}
                          />
                          {color.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label={t('settings.serviceCategories.mobileRoute')}
                    value={formData.rotaMobile}
                    onChange={(e) => setFormData({ ...formData, rotaMobile: e.target.value })}
                    placeholder="Ex: /recargas"
                    helperText={t('settings.serviceCategories.mobileRouteHelper')}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 0.5 }}>
                    Override do Formulário de Pagamento (opcional)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Se preenchido, substitui o label/hint do tipo de serviço nesta categoria
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Label do Campo Referência"
                    value={formData.referenceLabel || ''}
                    onChange={(e) => setFormData({ ...formData, referenceLabel: e.target.value })}
                    placeholder="Ex: Referência do Contador"
                    helperText="Vazio = usa o label do tipo de serviço"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Placeholder (Hint)"
                    value={formData.referenceHint || ''}
                    onChange={(e) => setFormData({ ...formData, referenceHint: e.target.value })}
                    placeholder="Ex: Insira a referência do contador"
                    helperText="Vazio = usa o hint do tipo de serviço"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Icone da Categoria
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', p: 1, border: '1px solid #ddd', borderRadius: 2, maxHeight: 200, overflow: 'auto' }}>
                    {[
                      { name: 'smartphone', label: 'Recargas' },
                      { name: 'speed', label: 'Contadores' },
                      { name: 'bolt', label: 'Energia' },
                      { name: 'phone_android', label: 'Telefone' },
                      { name: 'wifi', label: 'Internet' },
                      { name: 'water_drop', label: 'Água' },
                      { name: 'local_gas_station', label: 'Gás' },
                      { name: 'confirmation_num', label: 'Eventos' },
                      { name: 'volunteer_activism', label: 'Doações' },
                      { name: 'shield', label: 'Seguro' },
                      { name: 'account_balance', label: 'eCidadão' },
                      { name: 'shopping_bag', label: 'Compras' },
                      { name: 'restaurant', label: 'Restaurantes' },
                      { name: 'store', label: 'Lojas' },
                      { name: 'receipt_long', label: 'Faturas' },
                      { name: 'credit_card', label: 'Cartões' },
                      { name: 'payments', label: 'Pagamentos' },
                      { name: 'school', label: 'Educação' },
                      { name: 'local_hospital', label: 'Saúde' },
                      { name: 'directions_bus', label: 'Transporte' },
                      { name: 'flight', label: 'Viagens' },
                      { name: 'sports_soccer', label: 'Desporto' },
                      { name: 'music_note', label: 'Música' },
                      { name: 'groups', label: 'Quotas' },
                      { name: 'home', label: 'Casa' },
                      { name: 'pets', label: 'Animais' },
                      { name: 'eco', label: 'Ambiente' },
                      { name: 'church', label: 'Igreja' },
                      { name: 'apps', label: 'Serviços' },
                      { name: 'miscellaneous_services', label: 'Outros' },
                    ].map((icon) => (
                      <Box
                        key={icon.name}
                        onClick={() => setFormData({ ...formData, iconeUrl: icon.name })}
                        sx={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                          p: 1, borderRadius: 1, cursor: 'pointer', minWidth: 64,
                          border: formData.iconeUrl === icon.name ? '2px solid' : '1px solid transparent',
                          borderColor: formData.iconeUrl === icon.name ? 'primary.main' : 'transparent',
                          bgcolor: formData.iconeUrl === icon.name ? 'primary.light' : 'action.hover',
                          '&:hover': { bgcolor: 'action.selected' },
                        }}
                      >
                        <Box component="span" sx={{ fontSize: 24, color: formData.cor || '#6B2FA0', display: 'flex' }}>
                          {(() => {
                            const iconMap: Record<string, React.ReactNode> = {
                              smartphone: <SmartphoneIcon />, speed: <SpeedIcon />, bolt: <BoltIcon />,
                              phone_android: <PhoneAndroidIcon />, wifi: <WifiIcon />, water_drop: <WaterDropIcon />,
                              local_gas_station: <LocalGasStationIcon />, confirmation_num: <ConfirmationNumIcon />,
                              volunteer_activism: <VolunteerActivismIcon />, shield: <ShieldIcon />,
                              account_balance: <AccountBalanceIcon />, shopping_bag: <ShoppingBagIcon />,
                              restaurant: <RestaurantIcon />, store: <StoreIcon />, receipt_long: <ReceiptLongIcon />,
                              credit_card: <CreditCardIcon />, payments: <PaymentsIcon />, school: <SchoolIcon />,
                              local_hospital: <LocalHospitalIcon />, directions_bus: <DirectionsBusIcon />,
                              flight: <FlightIcon />, sports_soccer: <SportsSoccerIcon />, music_note: <MusicNoteIcon />,
                              groups: <GroupsIcon />, home: <HomeIcon />, pets: <PetsIcon />, eco: <EcoIcon />,
                              church: <ChurchIcon />, apps: <AppsIcon />, miscellaneous_services: <MiscellaneousServicesIcon />,
                            };
                            return iconMap[icon.name] || <AppsIcon />;
                          })()}
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: 9 }}>{icon.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 1 }}>
                    <TextField
                      fullWidth
                      label="Icone selecionado"
                      value={formData.iconeUrl}
                      onChange={(e) => setFormData({ ...formData, iconeUrl: e.target.value })}
                      placeholder="nome do icone"
                      helperText="Selecione acima ou digite o nome do Material Icon"
                    />
                    {formData.iconeUrl && (
                      <Box sx={{ minWidth: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider', bgcolor: formData.cor ? `${formData.cor}20` : 'action.hover' }}>
                        <img src={formData.iconeUrl} alt="icon" style={{ width: 28, height: 28, objectFit: 'contain' }} onError={(e: any) => { e.target.style.display = 'none'; }} />
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.publicadoMobile}
                        onChange={(e) => setFormData({ ...formData, publicadoMobile: e.target.checked })}
                        color="primary"
                      />
                    }
                    label={t('settings.serviceCategories.publishedMobile')}
                  />
                </Grid>
            </>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.nome || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {saving ? t('common.saving') : selectedCategory ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceCategories;
